import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useDayClosingVideoConsultationAPI } from "@/app/hooks/api_hooks/dayClosing/useDayClosingVideoConsultationAPI";
import { useDayClosingVideoConsultationCompletePostAPI } from "@/app/hooks/api_hooks/dayClosing/useDayClosingVideoConsultationCompletePostAPI";
import { useDayClosingVideoConsultationPostAPI } from "@/app/hooks/api_hooks/dayClosing/useDayClosingVideoConsultationPostAPI";
import { useAPIUtils } from "@/app/hooks/useAPIUtils";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useUtils } from "@/app/hooks/useUtils";
import { useEffect, useRef, useState } from "react";
import VideoConsultationCancelModal from "../VideoConsultationCancelModal";

const VideoConsultationClosingComponent = () => {
  const { execute: fetchVideoConsultationData, videoConsultationData } = useDayClosingVideoConsultationAPI();
  const { execute: postVideoConsultationCancelAction } = useDayClosingVideoConsultationPostAPI();
  const { execute: postVideoConsultationCompleteAction } = useDayClosingVideoConsultationCompletePostAPI();
  const queryParams = new URLSearchParams(location.search);
  const isLoading = useLoader("isClosingDataInProgress");
  const { NO_APPOINTMENTS } = useImageConstants();
  const { convertToReadableFormat, veticDateFormat } = useUtils();
  const { toastError } = useAPIUtils();

  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const processId = queryParams.get("closing_process") ?? "";
  const queryCity = queryParams.get("city_id");
  const time = queryParams.get("t") ?? "";

  const lastRequestKeyRef = useRef("");
  const [openCancelForBookingId, setOpenCancelForBookingId] = useState<string | null>(null);

  useEffect(() => {
    if (!queryClinics) return;
    const requestKey = `${queryClinics}-${queryShift}-${processId}-${queryCity}-${time}`;
    if (lastRequestKeyRef.current === requestKey) return;
    lastRequestKeyRef.current = requestKey;
    fetchVideoConsultationData(queryClinics, queryShift);
  }, [fetchVideoConsultationData, processId, queryCity, queryClinics, queryShift, time]);

  const clinicsWithConsultations =
    videoConsultationData?.filter((clinic) => clinic?.closure_list?.length > 0) || [];

  const closeCancelModal = () => {
    setOpenCancelForBookingId(null);
  };

  const handleComplete = async (bookingId?: string, pendencyReasons?: string[] | null) => {
    if (!bookingId || !queryClinics) return;
    if (pendencyReasons?.length) {
      toastError(`Unable to complete because ${pendencyReasons[0]}`);
      return;
    }
    const result = await postVideoConsultationCompleteAction({
      booking_id: bookingId,
    });
    if (result?.status) {
      closeCancelModal();
      fetchVideoConsultationData(queryClinics, queryShift);
    }
  };

  return (
    <div className="w-full">
      {isLoading ? (
        <TableSkeletonComponent columnLength={8} />
      ) : clinicsWithConsultations?.length > 0 ? (
        <div className="space-y-3 overflow-y-auto max-h-[650px]">
          {clinicsWithConsultations.map((VCData) => (
            <div
              key={VCData?.clinic_details?.clinic_id ?? VCData?.clinic_details?.clinic_name}
              className="rounded-2xl border border-[#ECEEF2] bg-white p-3 shadow-sm"
            >
              <div className="overflow-x-auto rounded-xl">
                <table className="w-full min-w-[1100px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                  <thead className="bg-[#F5F5F5] text-[#1F1F23] text-xs font-normal">
                    <tr>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        User Name
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Doctor Name
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Booking Time
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Booking Status
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-xs">
                    {VCData?.closure_list?.map((consultationData, rowIndex) => {
                      const isFirst = rowIndex === 0;
                      const isLast = rowIndex === VCData?.closure_list?.length - 1;
                      const isSingle = VCData?.closure_list?.length === 1;

                      return (
                        <tr
                          key={consultationData?.teleconsulation_id}
                          className={`
                            bg-white hover:bg-[#FCFCFD] transition-colors
                            ${isSingle ? "rounded-xl" : ""}
                            ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                            ${isLast && !isSingle ? "rounded-b-xl" : ""}
                          `}
                        >
                          <td
                            className={`
                              p-3 text-xs text-[#1F1F23]
                              ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                              ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                              ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                            `}
                          >
                            <div className="flex flex-col gap-1">
                              <p>{consultationData?.customer_details?.name || "-"}</p>
                            </div>
                          </td>
                          <td className="p-3 text-xs">
                            <div className="flex flex-col gap-1">
                              <p>{consultationData?.doctor_name || "-"}</p>
                            </div>
                          </td>
                          <td className="p-3 text-xs whitespace-nowrap">
                            {veticDateFormat(consultationData?.appointment_start_time || "-")}
                          </td>
                          <td className="p-3 text-xs whitespace-nowrap capitalize">
                            {consultationData?.booking_status
                              ? convertToReadableFormat(consultationData?.booking_status)
                              : "-"}
                          </td>
                          <td
                            className={`
                              p-3 text-xs whitespace-nowrap
                              ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                              ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                              ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                            `}
                          >
                            {!consultationData?.closure_status ? (
                              <div className="flex items-start gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleComplete(
                                      consultationData?.booking_id,
                                      consultationData?.pendency_reason ?? null,
                                    )
                                  }
                                  className="bg-black px-4 py-2 rounded-lg text-xs text-white font-medium whitespace-nowrap hover:opacity-90 transition-opacity"
                                >
                                  Complete
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!consultationData?.booking_id) return;
                                    setOpenCancelForBookingId(consultationData.booking_id);
                                  }}
                                  className="border border-[#D1D5DB] bg-white px-4 py-2 rounded-lg text-xs text-[#111827] font-medium whitespace-nowrap hover:bg-[#F9FAFB] transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Video Consultations Found" />
      )}

      <VideoConsultationCancelModal
        open={Boolean(openCancelForBookingId)}
        onClose={closeCancelModal}
        onSubmit={async (reason) => {
          if (!openCancelForBookingId || !queryClinics) return;

          const result = await postVideoConsultationCancelAction({
            booking_id: openCancelForBookingId,
            exception_reason: reason,
          });

          if (result?.status) {
            closeCancelModal();
            fetchVideoConsultationData(queryClinics, queryShift);
          }
        }}
      />
    </div>
  );
};

export default VideoConsultationClosingComponent;
